<template>
  <div class="social-container" :style="socialContainerStyle">
    <IconWrapper :name="iconName" :style="iconStyle" />
    <div :class="textClass">{{ formattedValue }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import IconWrapper from '../icons/IconWrapper.vue'
import { SocialType, SocialVariant } from '@/interfaces'

const props = withDefaults(
  defineProps<{
    type: SocialType
    total: number
    variant?: SocialVariant
  }>(),
  {
    total: 0,
    variant: SocialVariant.cardBottom
  }
)
const isList = computed(() => props.variant === SocialVariant.list)
const iconName = computed(() => {
  const iconMap = {
    [SocialType.Likes]: props.variant === SocialVariant.cardTop ? 'likesWhiteIcon' : 'likesIcon',
    [SocialType.Shares]: props.variant === SocialVariant.cardTop ? 'sharesWhiteIcon' : 'sharesIcon',
    [SocialType.Views]: props.variant === SocialVariant.cardTop ? 'viewsWhiteIcon' : 'viewsIcon'
  }
  return iconMap[props.type]
})

const iconStyle = computed(() => (isList.value ? { width: '22px', height: '22px' } : {}))

const socialContainerStyle = computed(() => (isList.value ? { gap: '8px' } : {}))

const textClass = computed(() => {
  if (isList.value) return 'text-list'
  return props.variant === SocialVariant.cardTop ? 'text-top' : 'text-bottom'
})

const formattedValue = computed(() => {
  const value = props.total
  if (value >= 1_000_000) {
    const millions = value / 1_000_000
    return Number.isInteger(millions) ? `${millions}M` : `${millions.toFixed(1)}M`
  } else if (value >= 1_000) {
    const thousands = Math.floor(value / 1_000)
    const remainder = value % 1_000
    return remainder === 0 ? `${thousands}K` : `${thousands}.${Math.floor((remainder / 1_000) * 10)}K`
  }
  return value.toString()
})
</script>

<style lang="scss" scoped>
.social-container {
  display: flex;
  gap: 2px;
  margin-left: 8px;
}

.text-bottom {
  color: $secondaryTitleText;
  font-size: 12px;
  font-weight: 400;
}

.text-top {
  color: $white;
  font-size: 10px;
  font-weight: 400;
}

.text-list {
  color: $bodyText;
  font-size: 14px;
  font-weight: 400;
}
</style>
